import { z } from 'zod';

// Schemas mirror the backend's RegisterTenantCommand / LoginCommand /
// AuthResultDto / UserDto wire shapes. Codes for shared constraints
// (min password length, max email length) match the FluentValidation
// rules on the server so the frontend rejects bad input before a round-trip.

export const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

export type LoginInput = z.infer<typeof loginSchema>;

// Signup is email + password ONLY. Organization details (name, size,
// website, industry, logo) are captured during onboarding via
// useCreateTenant once the user picks "Organization" on the stepper --
// the post-2026-05-15 redesign that mirrors html/signup.html exactly.
export const signupSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Enter a valid email')
    .max(320, 'Email is too long'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(256, 'Password is too long'),
  // boolean + refine so RHF's defaultValues can use `false`. Only `true`
  // passes validation -- catches the unchecked Terms checkbox.
  agreeToTerms: z
    .boolean()
    .refine((v) => v === true, { message: 'You must accept the Terms of Service' }),
});

export type SignupInput = z.infer<typeof signupSchema>;

export const forgotPasswordSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
});

export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .max(256, 'Password is too long'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

// UserDto — derived TenantId comes from the user's earliest tenant membership.
// Null for SuperAdmin and platform-only users. `onboardingCompleted` and
// `isEmailVerified` are server-side flags driving different post-auth
// behaviour:
//   * onboardingCompleted=false -> route to /onboarding/personal-details
//   * isEmailVerified=false      -> render the red alert bar on Dashboard
export const userDtoSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  tenantId: z.string().uuid().nullable(),
  isSuperAdmin: z.boolean(),
  onboardingCompleted: z.boolean(),
  isEmailVerified: z.boolean(),
});

export type UserDto = z.infer<typeof userDtoSchema>;

export const authResultSchema = z.object({
  accessToken: z.string().min(1),
  expiresIn: z.number().int().positive(),
  refreshToken: z.string().min(1),
});

export type AuthResult = z.infer<typeof authResultSchema>;

export const accessTokenResultSchema = z.object({
  accessToken: z.string().min(1),
  expiresIn: z.number().int().positive(),
});

export type AccessTokenResult = z.infer<typeof accessTokenResultSchema>;
